/**
 * Выбор движения — с картинками, поиском и возможностью завести своё.
 *
 * Раньше жил внутри экрана «Правка тренировки» и умел одно: отфильтровать
 * подстрокой сорок каталожных движений. Этого хватало, пока в каталоге было
 * всё, что человек делает. Но работы на предплечья там нет вообще — ни
 * упражнения, ни мышечной группы, — и поиск честно отвечал «ничего не
 * нашлось», после чего человеку было некуда деться.
 *
 * Поэтому пустой результат здесь — не тупик, а развилка: тем же текстом, что
 * человек уже напечатал, предлагается завести своё движение.
 *
 * КАРТИНКИ. У каждой строки каталожного движения — миниатюра, и она отдельная
 * кнопка: посмотреть и выбрать не должны быть одним касанием (подробности — в
 * `ExerciseRow`). Просмотр открывается на месте списка.
 *
 * ПОДСКАЗКИ СВЕРХУ. Экран может передать короткий список движений с причинами
 * («Похожие движения» при замене, «Под задачу дня» при добавлении) и группы
 * дня. Считает их сервер; здесь они только показаны — тем же видом строк и с
 * одним общим просмотром, чтобы одно движение не открывалось двумя разными
 * способами.
 *
 * ПРО КЛАВИАТУРУ (раздел 13). Текстовое поле в вебвью Telegram на iOS
 * перекрывает пол-экрана, и между подходами это дорого. Цена принята
 * осознанно и минимизирована: поле одно, оно открывается только по явному
 * тапу, название в форму подставляется уже напечатанное, а мышечная группа и
 * снаряд выбираются тапами по чипам — без клавиатуры вообще.
 */

import { useState } from 'react'

import { archiveExercise, createExercise, type Catalog } from '../api/exercises'
import {
  BAR_CHOICES,
  DEFAULT_BAR,
  EQUIPMENT_KINDS,
  MUSCLE_GROUPS,
  type DayGroup,
  type Exercise,
} from '../api/types'
import { equipmentLabel, muscleLabel } from '../labels'
import { ExercisePeek, ExerciseRow } from './ExerciseRow'
import { TechniqueCredit } from './TechniqueCard'
import { ConfirmButton, Loader } from './ui'

/**
 * Сколько строк показываем сразу. Список прокручивается внутри карточки
 * (`.se-list` с ограниченной высотой), поэтому резать его коротко незачем:
 * на экране планирования человек листает справочник целиком.
 */
const PICKER_LIMIT = 40

/** Группа по умолчанию в форме — та, ради которой всё и затевалось. */
const DEFAULT_GROUP = 'forearms'
const DEFAULT_EQUIPMENT = 'dumbbell'

/** Движение, предложенное сервером, и причина — его же словами. */
export interface FeaturedExercise {
  exercise: Exercise
  reason: string
}

function errorText(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown })?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  return error instanceof Error && error.message ? error.message : fallback
}

/** Подпись группы дня: название и запас недельного коридора — числом сервера. */
function groupChipText(group: DayGroup): string {
  const name = muscleLabel(group.muscle_group)
  if (group.room_sets === null) return name
  return group.room_sets > 0 ? `${name} · ещё ${group.room_sets} подх.` : `${name} · неделя заполнена`
}

export function ExercisePicker({
  catalog,
  taken,
  onPick,
  disabled = false,
  allowCreate = true,
  pickLabel = 'Выбрать',
  featuredTitle,
  featured = [],
  featuredLoading = false,
  hint,
  groups,
}: {
  catalog: Catalog
  /** Движения, уже стоящие в тренировке: сервер отверг бы их с 409. */
  taken: Set<number>
  onPick: (exercise: Exercise) => void
  disabled?: boolean
  allowCreate?: boolean
  /** Надпись на кнопке выбора в просмотре движения. */
  pickLabel?: string
  featuredTitle?: string
  featured?: FeaturedExercise[]
  featuredLoading?: boolean
  /** Строка от сервера над списком: почему подсказок столько, сколько есть. */
  hint?: string
  /** Группы задачи дня — чипы-фильтры списка. */
  groups?: DayGroup[]
}) {
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [dropError, setDropError] = useState('')
  const [peek, setPeek] = useState<Exercise | null>(null)
  const [group, setGroup] = useState<string | null>(null)

  // Выбранная группа могла пропасть из ответа: подсказки пересчитались или не
  // приехали. Невидимый фильтр хуже любого — список молча урезан, а кнопки,
  // которой его снять, на экране нет.
  const activeGroup = groups?.some((g) => g.muscle_group === group) ? group : null

  const needle = query.trim().toLowerCase()
  const pool = catalog.selectable
  const found = pool.filter(
    (e) =>
      (!needle || e.name.toLowerCase().includes(needle)) &&
      (activeGroup === null || e.muscle_group === activeGroup),
  )
  const shown = found.slice(0, PICKER_LIMIT)

  if (catalog.loading && catalog.list.length === 0) {
    return <Loader label="Загружаю справочник…" />
  }

  if (creating) {
    return (
      <NewExerciseForm
        initialName={query.trim()}
        onCancel={() => setCreating(false)}
        onCreated={(exercise) => {
          setCreating(false)
          onPick(exercise)
        }}
      />
    )
  }

  if (peek) {
    const isTaken = taken.has(peek.id)
    return (
      <ExercisePeek
        exercise={peek}
        pickLabel={isTaken ? 'Уже в тренировке' : pickLabel}
        disabled={disabled || isTaken}
        onBack={() => setPeek(null)}
        onPick={() => {
          const chosen = peek
          setPeek(null)
          onPick(chosen)
        }}
      />
    )
  }

  return (
    <div>
      {hint && <div className="faint xp-hint">{hint}</div>}

      {featuredLoading && (
        <div className="faint xp-hint">
          {featuredTitle ? `${featuredTitle}: подбираю…` : 'Подбираю…'}
        </div>
      )}

      {featured.length > 0 && (
        <div className="se-suggest">
          {featuredTitle && <div className="tq-title">{featuredTitle}</div>}
          {/* Без собственной прокрутки: подсказок немного, а два прокручиваемых
              блока друг под другом на узком экране путают палец. */}
          <div className="xp-featured">
            {featured.map((item) => (
              <ExerciseRow
                key={item.exercise.id}
                exercise={item.exercise}
                reason={item.reason}
                pill={taken.has(item.exercise.id) ? 'уже в тренировке' : undefined}
                disabled={disabled || taken.has(item.exercise.id)}
                onPick={() => onPick(item.exercise)}
                onPeek={() => setPeek(item.exercise)}
              />
            ))}
          </div>
          <div className="faint se-suggest-hint">Или выбери сам:</div>
        </div>
      )}

      {groups && groups.length > 0 && (
        <div className="goal-picker xp-groups">
          <button
            type="button"
            className={`pill ${activeGroup === null ? 'accent' : ''}`}
            onClick={() => setGroup(null)}
          >
            Все
          </button>
          {groups.map((g) => (
            <button
              key={g.muscle_group}
              type="button"
              className={`pill ${activeGroup === g.muscle_group ? 'accent' : ''}`}
              onClick={() => setGroup((prev) => (prev === g.muscle_group ? null : g.muscle_group))}
            >
              {groupChipText(g)}
            </button>
          ))}
        </div>
      )}

      <input
        className="se-search"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Название движения: жим, тяга, присед…"
        autoComplete="off"
        spellCheck={false}
      />

      {shown.length === 0 ? (
        <div className="faint">
          {catalog.list.length === 0
            ? 'Справочник не загрузился — выбрать движение не из чего. Проверь связь и обнови экран.'
            : activeGroup !== null
              ? `В группе «${muscleLabel(activeGroup)}» такого нет. Нажми «Все» — поиск пойдёт по всему справочнику.`
              : 'Ничего не нашлось. Попробуй короче: «жим», «тяга» — или заведи своё.'}
        </div>
      ) : (
        /* Пока подсказки в пути, строки не выбираются: приехав, блок подсказок
           встанет НАД списком и сдвинет его под пальцем, а в «Заменить» выбор
           сразу меняет план на сервере. Ждать недолго — экран ждёт подсказку
           не дольше шести секунд, и «подбираю…» при этом видно. */
        <div className="se-list">
          {shown.map((ex) => (
            <ExerciseRow
              key={ex.id}
              exercise={ex}
              pill={taken.has(ex.id) ? 'уже в тренировке' : ex.is_custom ? 'своё' : undefined}
              disabled={disabled || featuredLoading || taken.has(ex.id)}
              onPick={() => onPick(ex)}
              onPeek={() => setPeek(ex)}
              extra={
                // Убрать можно только своё. Опечатка вроде «Молоки» иначе
                // осталась бы в списке навсегда и мозолила бы глаза при
                // каждом выборе. Это не удаление: подходы по движению
                // остаются, и «История» продолжит называть их словами.
                ex.is_custom ? (
                  <ConfirmButton
                    className="btn ghost se-drop"
                    confirmLabel="Убрать?"
                    onConfirm={() => {
                      setDropError('')
                      void archiveExercise(ex.id).catch((e: unknown) => {
                        // Молчаливый отказ здесь хуже всего: строка осталась
                        // на месте, и человек решит, что промахнулся, и
                        // нажмёт ещё раз.
                        setDropError(errorText(e, `Не удалось убрать «${ex.name}»`))
                      })
                    }}
                  >
                    ×
                  </ConfirmButton>
                ) : undefined
              }
            />
          ))}
        </div>
      )}

      {dropError && <div className="se-fail">{dropError}</div>}

      {found.length > shown.length && (
        <div className="faint" style={{ marginTop: 8 }}>
          Показаны первые {shown.length} из {found.length} — уточни запрос.
        </div>
      )}

      {/* Атрибуцию выводит тот же контейнер, что показывает миниатюры, — и
          по ВСЕМ показанным строкам, включая подсказки сверху. Иначе при
          пустом поиске миниатюры «Похожих движений» остались бы на экране
          без указания лицензии. */}
      <TechniqueCredit
        exerciseIds={[...featured.map((f) => f.exercise.id), ...shown.map((e) => e.id)]}
        withSources={false}
      />

      {allowCreate && (
        <button
          className="btn wide ghost"
          style={{ marginTop: 10 }}
          disabled={disabled}
          onClick={() => setCreating(true)}
        >
          {needle ? `Завести своё: «${query.trim()}»` : 'Завести своё движение'}
        </button>
      )}
    </div>
  )
}

/**
 * Форма своего движения: название, что качает, на чём.
 *
 * Три поля, и ни одного лишнего. Шаг веса, минимальный вес и «вес на одну
 * сторону» не спрашиваются: их выводит сервер из снаряда по соглашениям
 * каталога. Спросить это у человека значит задать три вопроса про числа,
 * которых он не знает.
 */
function NewExerciseForm({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName: string
  onCreated: (exercise: Exercise) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initialName)
  const [group, setGroup] = useState<string>(DEFAULT_GROUP)
  const [equipment, setEquipment] = useState<string>(DEFAULT_EQUIPMENT)
  const [compound, setCompound] = useState(false)
  const [bar, setBar] = useState<number>(DEFAULT_BAR)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const clean = name.trim()
  const tooShort = clean.length < 2

  async function submit(): Promise<void> {
    setBusy(true)
    setFailure('')
    try {
      const created = await createExercise({
        name: clean,
        muscle_group: group,
        equipment,
        is_compound: compound,
        // Только для штанги: у гантели «гриф» это первая гантель, и чужое
        // число там сломало бы округление рабочих весов.
        bar_weight: equipment === 'barbell' ? bar : null,
      })
      onCreated(created)
    } catch (e) {
      setFailure(errorText(e, 'Не удалось завести движение'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <input
        className="se-search"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Как называешь: молотки, сгибания на предплечья…"
        autoComplete="off"
        spellCheck={false}
        maxLength={128}
      />

      <div className="xf-label">Что качает</div>
      <div className="xf-chips">
        {MUSCLE_GROUPS.map((m) => (
          <button
            key={m}
            type="button"
            className={m === group ? 'chip on' : 'chip'}
            onClick={() => setGroup(m)}
          >
            {muscleLabel(m)}
          </button>
        ))}
      </div>

      <div className="xf-label">На чём</div>
      <div className="xf-chips">
        {EQUIPMENT_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={k === equipment ? 'chip on' : 'chip'}
            onClick={() => setEquipment(k)}
          >
            {equipmentLabel(k)}
          </button>
        ))}
      </div>

      {/* Гриф спрашивается только у штанги и только потому, что от него
          зависит и минимальный возможный вес, и то, как считаются блины на
          экране тренировки. Сменить его потом можно там же, одним тапом. */}
      {equipment === 'barbell' && (
        <>
          <div className="xf-label">Вес пустого грифа</div>
          <div className="xf-chips">
            {BAR_CHOICES.map((kg) => (
              <button
                key={kg}
                type="button"
                className={kg === bar ? 'chip on' : 'chip'}
                onClick={() => setBar(kg)}
              >
                {kg} кг
              </button>
            ))}
          </div>
        </>
      )}

      {/* Влияет ровно на одно: базовое движение отдыхает дольше (раздел 7). */}
      <button
        type="button"
        className={compound ? 'chip on xf-wide' : 'chip xf-wide'}
        onClick={() => setCompound((v) => !v)}
      >
        {compound ? '✓ ' : ''}Базовое движение — отдых дольше
      </button>

      <div className="faint" style={{ marginTop: 10 }}>
        {equipment === 'bodyweight'
          ? 'Свой вес: в подход пиши только дополнительное отягощение, 0 — без него.'
          : equipment === 'dumbbell'
            ? 'Гантели: вес пишется на одну руку, шаг 2 кг.'
            : equipment === 'barbell'
              ? 'Штанга: на тренировке останется набрать блины, гриф прибавится сам.'
              : 'Шаг веса подставит сервер — по тому, что реально можно собрать.'}
      </div>
      <div className="faint" style={{ marginTop: 6 }}>
        Вес на сегодня движок назначит после первых подходов: пока истории нет,
        придумывать его нельзя.
      </div>

      {failure && <div className="se-fail">{failure}</div>}

      <button
        className="btn wide primary"
        style={{ marginTop: 10 }}
        disabled={busy || tooShort}
        onClick={() => void submit()}
      >
        {busy ? 'Завожу…' : 'Завести и добавить'}
      </button>
      <button className="btn wide ghost" style={{ marginTop: 8 }} onClick={onCancel}>
        Назад к поиску
      </button>
    </div>
  )
}

export default ExercisePicker
